import React from 'react';
import {
  Alert,
  Box,
  FormHelperText,
  MenuItem,
  Stack,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './builder/TokenAwareTextField';
import { useCampaignMutations, useReportFigureConfig, useReportSectionConfig } from '../../../hooks/campaign-hooks';
import {
  CampaignReportFigure,
  CampaignReportSection,
  ReportFigureChartKind,
} from '../../../support/campaigns';
import {
  CAMPAIGN_DELIVERY_SOURCE,
  CampaignDeliveryDataset,
} from '../../../support/dataImport/campaignChartSource';
import {
  CampaignReportFigureStorageUnavailableError,
  uploadReportFigureImage,
} from '../../../support/campaignReportFigures';
import {
  BuilderRoot,
  Card,
  GhostButton,
  PrimaryButton,
  SectionHeader,
  StatusPill,
  builderStyles,
} from './builder/primitives';

/**
 * Management > Campaigns > Report Builder > Canvas.
 *
 * The admin authoring surface for a campaign's shared report *figures* — the charts and images that
 * fill the report's `slot`s. Every figure is a `CampaignReportFigure` row; the runner reads them
 * (`useReportFigures`), fills each chart with the *current* delivery's numbers, and renders the same
 * arrangement read-only for every SE (see `RecommendationStep`/`ReportEditor` `figuresLocked`). So a
 * campaign's report layout is authored once here, not per SE.
 *
 * A chart figure stores a `chart_query`: either a **Campaign Delivery** recipe (dataset + `groupBy` /
 * optional `valueBy` — the renderer fills the delivery id, never stored here, so the same figure
 * charts each SE's own delivery) or a **static** `bars[]` literal. An image figure stores an S3
 * `image_object_key` (bytes uploaded via `uploadReportFigureImage`; a signed URL is minted on read).
 *
 * `figure_id` is a stable minted id used by the render slot — never derived from array position.
 * Writes are full-record PUTs (Go runtime); reorder swaps `order` with the neighbour, mirroring the
 * section editor. Controls degrade to read-only when `canWrite` is false.
 */

const CHART_KINDS: Array<{ value: ReportFigureChartKind; label: string }> = [
  { value: 'bar', label: 'Bar' },
  { value: 'doughnut', label: 'Doughnut' },
  { value: 'line', label: 'Line' },
];

const TONE_OPTIONS = ['neutral', 'red', 'amber', 'green'] as const;

const DATASET_OPTIONS: Array<{ value: CampaignDeliveryDataset; label: string }> = [
  { value: 'step_status', label: 'Step status (completion per step)' },
  { value: 'target', label: 'Targets (per customer)' },
  { value: 'install_base', label: 'Install-base exposure (from the Cisco Ready export)' },
];

/**
 * The flattened row fields each dataset exposes for `groupBy` / `valueBy` (see campaignChartSource).
 * `install_base` is a campaign-resolved dataset with no queryable rows — the runner derives its bars
 * itself — so it has no group/value columns; the editor hides those controls for it (below).
 */
const GROUP_FIELDS: Record<CampaignDeliveryDataset, string[]> = {
  step_status: ['status', 'step', 'stepKind'],
  target: ['status', 'customer'],
  install_base: [],
};
const VALUE_FIELDS: Record<CampaignDeliveryDataset, string[]> = {
  step_status: ['', 'completed'],
  target: ['', 'count'],
  install_base: [],
};

/** A stable figure id, never the array index — it keys the render slot and survives reorder. */
function mintFigureId(): string {
  return `fig-${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-4)}`;
}

function blankChartFigure(
  campaignId: number,
  sectionId: string,
  order: number,
): Partial<CampaignReportFigure> {
  return {
    campaign: campaignId,
    section_id: sectionId,
    figure_id: mintFigureId(),
    kind: 'chart',
    order,
    anchor: null,
    title: 'New chart',
    caption: '',
    explanation: '',
    chart_kind: 'bar',
    tone: 'neutral',
    chart_query: {
      dataSource: CAMPAIGN_DELIVERY_SOURCE,
      campaignDeliveryDataset: 'step_status',
      groupBy: 'status',
      scale: 'count',
      source: '',
    },
    image_object_key: null,
    image_content_type: null,
    image_width: null,
    image_height: null,
    image_size_bytes: null,
    metadata: {},
  };
}

export function ReportCanvas(props: {
  campaignId: number;
  campaignName: string;
  canWrite: boolean;
  /** Back to the section list. Omitted when embedded (the parent Layout view owns navigation). */
  onBack?: () => void;
  /**
   * Rendered inside the combined Layout view (below the section grid) rather than as a standalone
   * page: drops the `BuilderRoot` wrapper (the parent supplies one) and the back button, and shows a
   * compact sub-heading. The add-chart/add-image actions stay.
   */
  embedded?: boolean;
}): JSX.Element {
  const { campaignId, campaignName, canWrite, onBack, embedded } = props;
  const { data: figureRows, isLoading } = useReportFigureConfig(campaignId);
  const { data: sectionRows } = useReportSectionConfig(campaignId);
  const { create, update, remove, isSaving, error } = useCampaignMutations('reportFigure');

  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const sections = React.useMemo(
    () => ((sectionRows ?? []) as CampaignReportSection[]).slice().sort((a, b) => a.order - b.order),
    [sectionRows],
  );
  const rows = React.useMemo(
    () => ((figureRows ?? []) as CampaignReportFigure[]).slice().sort((a, b) => a.order - b.order),
    [figureRows],
  );

  // The section a new figure lands in, and where an uploaded image attaches. Default to the first
  // declared section (or the report engine's fallback id) so a figure is never orphaned.
  const defaultSectionId = sections[0]?.section_id ?? 'recommendation';
  const nextOrder = rows.length ? Math.max(...rows.map((r) => r.order)) + 1 : 0;

  // Full-record PUT (Go 400s on a delta): merge the change onto the whole row.
  const saveField = React.useCallback(
    (row: CampaignReportFigure, patch: Partial<CampaignReportFigure>) => {
      update.mutate({ ...row, ...patch });
    },
    [update],
  );

  // Reorder swaps `order` with the neighbour and PUTs both whole rows (mirrors SectionEditor.move).
  const move = React.useCallback(
    (index: number, dir: -1 | 1) => {
      const a = rows[index];
      const b = rows[index + dir];
      if (!a || !b) return;
      update.mutate({ ...a, order: b.order });
      update.mutate({ ...b, order: a.order });
    },
    [rows, update],
  );

  const addChart = React.useCallback(() => {
    create.mutate(blankChartFigure(campaignId, defaultSectionId, nextOrder));
  }, [create, campaignId, defaultSectionId, nextOrder]);

  const onPickImage = React.useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      setUploadError(null);
      setUploading(true);
      try {
        const up = await uploadReportFigureImage(campaignId, file);
        create.mutate({
          campaign: campaignId,
          section_id: defaultSectionId,
          figure_id: mintFigureId(),
          kind: 'image',
          order: nextOrder,
          anchor: null,
          title: file.name.replace(/\.[^.]+$/, ''),
          caption: '',
          explanation: '',
          chart_kind: 'bar',
          tone: 'neutral',
          chart_query: {},
          image_object_key: up.object_key,
          image_content_type: up.content_type,
          image_width: up.width,
          image_height: up.height,
          image_size_bytes: up.size_bytes,
          metadata: {},
        });
      } catch (e) {
        setUploadError(
          e instanceof CampaignReportFigureStorageUnavailableError
            ? 'Image storage is not configured for this environment yet — the chart figures work, but image upload is unavailable.'
            : (e as Error)?.message ?? 'Upload failed.',
        );
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [campaignId, create, defaultSectionId, nextOrder],
  );

  const actions = (
    <Stack direction="row" spacing={1}>
      {!embedded && <GhostButton onClick={onBack}>← Sections</GhostButton>}
      {canWrite && (
        <>
          <PrimaryButton onClick={addChart} disabled={isSaving}>
            + Add chart
          </PrimaryButton>
          <PrimaryButton
            onClick={() => fileInputRef.current?.click()}
            disabled={isSaving || uploading}
          >
            {uploading ? 'Uploading…' : '+ Add image'}
          </PrimaryButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => onPickImage(e.target.files?.[0])}
          />
        </>
      )}
    </Stack>
  );

  const body = (
    <>
      {error != null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error)?.message ?? 'Save failed.'}
        </Alert>
      )}
      {uploadError != null && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {isLoading && <p className={builderStyles.lead}>Loading figures…</p>}
      {!isLoading && rows.length === 0 && (
        <p className={builderStyles.lead}>
          No figures yet — this campaign&apos;s report uses the runner&apos;s default figures.{' '}
          {canWrite ? 'Add a chart or image to author a shared canvas.' : ''}
        </p>
      )}

      <Stack spacing={2}>
        {rows.map((row, index) => (
          <FigureCard
            key={row.id}
            row={row}
            index={index}
            total={rows.length}
            sections={sections}
            canWrite={canWrite}
            isSaving={isSaving}
            onSave={saveField}
            onMove={move}
            onDelete={() => remove.mutate(row.id)}
          />
        ))}
      </Stack>
    </>
  );

  if (embedded) {
    return (
      <Box component="section">
        <SectionHeader
          compact
          title="Figures"
          subtitle="Add and arrange the report's charts and images. Charts pull each delivery's own numbers at render time; the layout is read-only for SEs."
          actions={actions}
        />
        {body}
      </Box>
    );
  }

  return (
    <BuilderRoot>
      <SectionHeader
        title={`Report Canvas — ${campaignName}`}
        subtitle="Arrange the shared report figures. Charts pull each delivery's own numbers at render time; the layout is read-only for SEs."
        actions={actions}
      />
      {body}
    </BuilderRoot>
  );
}

/** One figure's editable card — section/anchor/prose plus a chart-query or image-key sub-editor. */
function FigureCard(props: {
  row: CampaignReportFigure;
  index: number;
  total: number;
  sections: CampaignReportSection[];
  canWrite: boolean;
  isSaving: boolean;
  onSave: (row: CampaignReportFigure, patch: Partial<CampaignReportFigure>) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onDelete: () => void;
}): JSX.Element {
  const { row, index, total, sections, canWrite, isSaving, onSave, onMove, onDelete } = props;

  return (
    <Card className={builderStyles.contentCard}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <span className={builderStyles.tag}>{row.figure_id}</span>
        <StatusPill tone={row.kind === 'chart' ? 'progress' : 'done'}>{row.kind}</StatusPill>
        <span style={{ marginLeft: 'auto' }} />
        {canWrite && (
          <>
            <GhostButton onClick={() => onMove(index, -1)} disabled={index === 0 || isSaving}>
              ↑
            </GhostButton>
            <GhostButton onClick={() => onMove(index, 1)} disabled={index === total - 1 || isSaving}>
              ↓
            </GhostButton>
            <GhostButton onClick={onDelete} disabled={isSaving}>
              Delete
            </GhostButton>
          </>
        )}
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          select
          label="Section"
          size="small"
          value={row.section_id}
          disabled={!canWrite}
          onChange={(e) => onSave(row, { section_id: e.target.value })}
          sx={{ minWidth: 220 }}
          helperText="Which report section this figure sits in"
        >
          {sections.length === 0 && <MenuItem value={row.section_id}>{row.section_id}</MenuItem>}
          {sections.map((s) => (
            <MenuItem key={s.id} value={s.section_id}>
              {s.title || s.section_id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Title"
          size="small"
          fullWidth
          defaultValue={row.title}
          disabled={!canWrite}
          onBlur={(e) => e.target.value !== row.title && onSave(row, { title: e.target.value })}
        />
        <TextField
          label="Anchor gap"
          size="small"
          type="number"
          defaultValue={row.anchor ?? ''}
          disabled={!canWrite}
          helperText="Gap index in the section (blank = end)"
          onBlur={(e) => {
            const next = e.target.value === '' ? null : Number(e.target.value);
            if (next !== row.anchor) onSave(row, { anchor: next });
          }}
          sx={{ width: 140 }}
        />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
        <TextField
          label="Caption"
          size="small"
          fullWidth
          defaultValue={row.caption}
          disabled={!canWrite}
          onBlur={(e) => e.target.value !== row.caption && onSave(row, { caption: e.target.value })}
        />
        <TextField
          label="Explanation"
          size="small"
          fullWidth
          defaultValue={row.explanation}
          disabled={!canWrite}
          onBlur={(e) =>
            e.target.value !== row.explanation && onSave(row, { explanation: e.target.value })
          }
        />
      </Stack>

      {row.kind === 'chart' ? (
        <ChartQueryEditor row={row} canWrite={canWrite} onSave={onSave} />
      ) : (
        <ImageFigureInfo row={row} />
      )}
    </Card>
  );
}

/** The chart-visual + `chart_query` sub-editor: Campaign Delivery recipe or a static bar list. */
function ChartQueryEditor(props: {
  row: CampaignReportFigure;
  canWrite: boolean;
  onSave: (row: CampaignReportFigure, patch: Partial<CampaignReportFigure>) => void;
}): JSX.Element {
  const { row, canWrite, onSave } = props;
  const q = (row.chart_query ?? {}) as Record<string, any>;
  const isDelivery = q.dataSource === CAMPAIGN_DELIVERY_SOURCE;
  const dataset: CampaignDeliveryDataset = q.campaignDeliveryDataset ?? 'step_status';

  // Merge a patch onto the whole query and PUT the whole row (Go full-record).
  const saveQuery = (patch: Record<string, unknown>) =>
    onSave(row, { chart_query: { ...q, ...patch } });

  const setStatic = () =>
    onSave(row, {
      chart_query: {
        source: q.source ?? '',
        scale: q.scale ?? 'count',
        unit: q.unit ?? '',
        bars: Array.isArray(q.bars) ? q.bars : [{ label: 'Item', value: 1, tone: 'neutral' }],
      },
    });
  const setDelivery = () =>
    onSave(row, {
      chart_query: {
        dataSource: CAMPAIGN_DELIVERY_SOURCE,
        campaignDeliveryDataset: dataset,
        groupBy: q.groupBy ?? GROUP_FIELDS[dataset][0],
        valueBy: q.valueBy ?? '',
        scale: q.scale ?? 'count',
        unit: q.unit ?? '',
        source: q.source ?? '',
      },
    });

  return (
    <Stack spacing={2} sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
        <TextField
          select
          label="Chart style"
          size="small"
          value={row.chart_kind}
          disabled={!canWrite}
          onChange={(e) => onSave(row, { chart_kind: e.target.value as ReportFigureChartKind })}
          sx={{ minWidth: 140 }}
        >
          {CHART_KINDS.map((k) => (
            <MenuItem key={k.value} value={k.value}>
              {k.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Data"
          size="small"
          value={isDelivery ? 'delivery' : 'static'}
          disabled={!canWrite}
          onChange={(e) => (e.target.value === 'delivery' ? setDelivery() : setStatic())}
          sx={{ minWidth: 200 }}
          helperText={isDelivery ? "This delivery's live rows" : 'Fixed bars you type in'}
        >
          <MenuItem value="delivery">Campaign delivery (per SE)</MenuItem>
          <MenuItem value="static">Static bars</MenuItem>
        </TextField>
        <TextField
          label="Source label"
          size="small"
          defaultValue={q.source ?? ''}
          disabled={!canWrite}
          onBlur={(e) => (e.target.value !== (q.source ?? '')) && saveQuery({ source: e.target.value })}
          helperText="Shown under the chart"
          sx={{ minWidth: 200 }}
        />
      </Stack>

      {isDelivery ? (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Dataset"
            size="small"
            value={dataset}
            disabled={!canWrite}
            onChange={(e) =>
              saveQuery({
                campaignDeliveryDataset: e.target.value as CampaignDeliveryDataset,
                groupBy: GROUP_FIELDS[e.target.value as CampaignDeliveryDataset][0],
                valueBy: '',
              })
            }
            sx={{ minWidth: 260 }}
          >
            {DATASET_OPTIONS.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </TextField>
          {dataset === 'install_base' ? (
            // Derived by the runner from each delivery's parsed Cisco Ready export — there are no
            // group/value columns to pick, so only the title/explanation/placement are editable.
            <FormHelperText sx={{ alignSelf: 'center' }}>
              Bars are derived automatically from this delivery&rsquo;s install-base export.
            </FormHelperText>
          ) : (
            <>
              <TextField
                select
                label="Group by"
                size="small"
                value={q.groupBy ?? GROUP_FIELDS[dataset][0]}
                disabled={!canWrite}
                onChange={(e) => saveQuery({ groupBy: e.target.value })}
                sx={{ minWidth: 160 }}
                helperText="One bar per value"
              >
                {GROUP_FIELDS[dataset].map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Sum field"
                size="small"
                value={q.valueBy ?? ''}
                disabled={!canWrite}
                onChange={(e) => saveQuery({ valueBy: e.target.value })}
                sx={{ minWidth: 160 }}
                helperText="Blank = count rows"
              >
                {VALUE_FIELDS[dataset].map((f) => (
                  <MenuItem key={f || 'count'} value={f}>
                    {f || 'Count of rows'}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </Stack>
      ) : (
        <StaticBarsEditor bars={Array.isArray(q.bars) ? q.bars : []} canWrite={canWrite} onChange={(bars) => saveQuery({ bars })} />
      )}
    </Stack>
  );
}

/** Editor for a static chart's literal bars (label / value / tone rows). */
function StaticBarsEditor(props: {
  bars: any[];
  canWrite: boolean;
  onChange: (bars: any[]) => void;
}): JSX.Element {
  const { bars, canWrite, onChange } = props;
  const patch = (i: number, next: Record<string, unknown>) =>
    onChange(bars.map((b, idx) => (idx === i ? { ...b, ...next } : b)));

  return (
    <Stack spacing={1}>
      {bars.map((bar, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <TextField
            label="Label"
            size="small"
            defaultValue={bar?.label ?? ''}
            disabled={!canWrite}
            onBlur={(e) => e.target.value !== (bar?.label ?? '') && patch(i, { label: e.target.value })}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Value"
            size="small"
            type="number"
            defaultValue={bar?.value ?? 0}
            disabled={!canWrite}
            onBlur={(e) => Number(e.target.value) !== bar?.value && patch(i, { value: Number(e.target.value) || 0 })}
            sx={{ width: 110 }}
          />
          <TextField
            select
            label="Tone"
            size="small"
            value={TONE_OPTIONS.includes(bar?.tone) ? bar.tone : 'neutral'}
            disabled={!canWrite}
            onChange={(e) => patch(i, { tone: e.target.value })}
            sx={{ width: 120 }}
          >
            {TONE_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          {canWrite && (
            <GhostButton onClick={() => onChange(bars.filter((_, idx) => idx !== i))}>✕</GhostButton>
          )}
        </Stack>
      ))}
      {canWrite && (
        <GhostButton onClick={() => onChange([...bars, { label: 'Item', value: 1, tone: 'neutral' }])}>
          + Add bar
        </GhostButton>
      )}
    </Stack>
  );
}

/** Read-only summary of an uploaded image figure — the bytes live in S3, resolved to a URL on read. */
function ImageFigureInfo(props: { row: CampaignReportFigure }): JSX.Element {
  const { row } = props;
  return (
    <Alert severity="info" icon={false} sx={{ mt: 2 }}>
      <strong>Image:</strong> {row.image_object_key ?? 'not uploaded'}
      {row.image_width && row.image_height ? ` · ${row.image_width}×${row.image_height}px` : ''}
      {row.image_size_bytes ? ` · ${Math.max(1, Math.round(row.image_size_bytes / 1024))} KB` : ''}
    </Alert>
  );
}

export default ReportCanvas;
